<template>
  <DataTable
    :value="[offerSpin]"
    table-style="margin-top: 1rem"
    :style="{ width: offerSubtype === 'Pay first' ? '40%' : '100%' }"
  >
    <Column
      style="width: 40%"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
      }"
    >
      <template #header>
        <div :class="$style.priceHeader">
          <span>Price iOS</span>
          <span>Price Android</span>
        </div>
      </template>
      <template #body="{ data }">
        <PriceTypeDropdown
          :table-data="data"
          :selected-price-type="data?.priceType"
          offer-version="V2"
          :is-editable="isEditable"
          @update-iap-id="(event) => handleIapIdChange(event, data)"
          @update-rv-amount="(event) => handleRvAmountChange(event, data)"
          @update-in-game-price="(event) => handleInGamePriceChange(event, data)"
          @update-price-type="(event) => handlePriceTypeChange(event)"
          @update-free-reward="() => handleFreeRewardChange(data)"
        />
      </template>
    </Column>
    <Column
      v-if="offerSubtype === 'Pay later'"
      style="width: 40%"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
      }"
    >
      <template #header>
        <div :class="$style.priceHeader">
          <span>Re-spin iOS price</span>
          <span>Re-spin Android price</span>
        </div>
      </template>
      <template #body="{ data }">
        <PriceTypeDropdown
          :table-data="data"
          :selected-price-type="data?.reRollPriceType"
          offer-version="V2"
          is-pay-later
          :is-editable="isEditable"
          @update-iap-id="(event) => handleIapIdChange(event, data, true)"
          @update-rv-amount="(event) => handleRvAmountChange(event, data, true)"
          @update-in-game-price="(event) => handleInGamePriceChange(event, data, true)"
          @update-price-type="(event) => handlePriceTypeChange(event)"
          @update-free-reward="() => handleFreeRewardChange(data, true)"
        />
      </template>
    </Column>
    <Column
      v-if="offerSubtype === 'Pay later'"
      style="width: 20%"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
      }"
    >
      <template #header>
        <div>Spin limit</div>
      </template>
      <template #body="{ data }">
        <div :class="$style.spinLimit" @click="(event) => toggleSpinLimitPanel(event, data.reRollLimit)">
          <span>
            {{ data.reRollLimit }}
          </span>
          <VdIcon store="fa" name="chevron-down" size="small" color="var(--vd-color-text-light-2)"></VdIcon>
        </div>
        <Popover ref="spinLimitPanel">
          <div :class="$style.spinLimitForm">
            <InputNumber v-model="spinLimit" placeholder="Spin limit" :min="0" />
            <Button label="Save" @click="() => handleSpinLimitChange(data)" />
          </div>
        </Popover>
      </template>
    </Column>
  </DataTable>
</template>

<script setup lang="ts">
import { OfferSpin, OfferSpinInput, SellableProductPriceType, useUpdateOfferSpinMutation } from "@/graphql";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputNumber from "primevue/inputnumber";
import Popover from "primevue/popover";
import { PropType, ref } from "vue";
import { useRoute } from "vue-router";

import PriceTypeDropdown from "../components/PriceTypeDropdown/PriceTypeDropdown.vue";
import { mapOfferSpinInput } from "../utils";

const emit = defineEmits(["refetch-offer-spin"]);

const props = defineProps({
  offerId: {
    type: String,
    required: true,
  },
  offerSpin: {
    type: Object as PropType<OfferSpin | null>,
    required: true,
  },
  isEditable: {
    type: Boolean,
    required: true,
  },
  offerSubtype: {
    type: String,
    required: true,
  },
});

const route = useRoute();
const appId = ref<string>(route.params.gameId as string);
const versionId = ref<string>(route.params.versionId as string);
const selectedPriceType = ref(SellableProductPriceType.Iap);
const spinLimit = ref(0);
const spinLimitPanel = ref<InstanceType<typeof Popover> | null>(null);

const apiRequestsPayload = {
  appId: appId.value,
  version: versionId.value,
  offerId: props.offerId,
};

const toggleSpinLimitPanel = (event: MouseEvent, reRollLimit: number) => {
  if (!props.isEditable) {
    return;
  }

  spinLimit.value = reRollLimit;
  spinLimitPanel.value?.toggle(event);
};

const { mutate: updateOfferSpin } = useUpdateOfferSpinMutation({});

const updateReward = async (offerSpinId: string, input: OfferSpinInput) => {
  try {
    await updateOfferSpin({
      ...apiRequestsPayload,
      offerSpinId,
      input: {
        id: offerSpinId,
        ...input,
      },
    });

    emit("refetch-offer-spin");
  } catch (error) {
    console.log(error, "error");
  }
};

const handleSpinLimitChange = async (offerSpin: OfferSpin) => {
  const payload = mapOfferSpinInput(offerSpin);
  payload.reRollLimit = spinLimit.value;

  await updateReward(offerSpin.id, payload);

  spinLimitPanel.value?.hide();
};

interface IapChangeEvent {
  ios: string;
  android: string;
}

const handleIapIdChange = async (event: IapChangeEvent, offerSpin: OfferSpin, isPayLater?: boolean) => {
  const payload = mapOfferSpinInput(offerSpin);

  if (isPayLater && payload.reRollIosPrice && payload.reRollAndroidPrice) {
    payload.reRollPriceType = SellableProductPriceType.Iap;
    payload.reRollIosPrice.IAPId = event.ios;
    payload.reRollAndroidPrice.IAPId = event.android;
    payload.reRollIosPrice.rvQuantity = null;
    payload.reRollAndroidPrice.rvQuantity = null;
    payload.reRollIosPrice.inGamePrice = null;
    payload.reRollAndroidPrice.inGamePrice = null;
  } else {
    payload.priceType = SellableProductPriceType.Iap;
    payload.iosPrice.IAPId = event.ios;
    payload.androidPrice.IAPId = event.android;
    payload.iosPrice.rvQuantity = null;
    payload.androidPrice.rvQuantity = null;
    payload.iosPrice.inGamePrice = null;
    payload.androidPrice.inGamePrice = null;
  }

  await updateReward(offerSpin.id, payload);
};

interface RvAmountChangeEvent {
  ios: number;
  android: number;
}

const handleRvAmountChange = async (event: RvAmountChangeEvent, offerSpin: OfferSpin, isPayLater?: boolean) => {
  const payload = mapOfferSpinInput(offerSpin);

  if (isPayLater && payload.reRollIosPrice && payload.reRollAndroidPrice) {
    payload.reRollPriceType = SellableProductPriceType.Rv;
    payload.reRollIosPrice.rvQuantity = event.ios;
    payload.reRollAndroidPrice.rvQuantity = event.android;
    payload.reRollIosPrice.inGamePrice = null;
    payload.reRollAndroidPrice.inGamePrice = null;
    payload.reRollIosPrice.IAPId = null;
    payload.reRollAndroidPrice.IAPId = null;
  } else {
    payload.priceType = SellableProductPriceType.Rv;
    payload.iosPrice.rvQuantity = event.ios;
    payload.androidPrice.rvQuantity = event.android;
    payload.iosPrice.inGamePrice = null;
    payload.androidPrice.inGamePrice = null;
    payload.iosPrice.IAPId = null;
    payload.androidPrice.IAPId = null;
  }

  await updateReward(offerSpin.id, payload);
};

interface Price {
  quantity: number;
  itemId: string;
}

interface PlatformPrices {
  ios: Price;
  android: Price;
}

const handleInGamePriceChange = async (price: PlatformPrices, offerSpin: OfferSpin, isPayLater?: boolean) => {
  const payload = mapOfferSpinInput(offerSpin);

  if (isPayLater && payload.reRollIosPrice && payload.reRollAndroidPrice) {
    payload.reRollPriceType = SellableProductPriceType.InGameCurrency;
    payload.reRollIosPrice.inGamePrice = price.ios;
    payload.reRollAndroidPrice.inGamePrice = price.android;
    payload.reRollIosPrice.IAPId = null;
    payload.reRollAndroidPrice.IAPId = null;
    payload.reRollIosPrice.rvQuantity = null;
    payload.reRollAndroidPrice.rvQuantity = null;
  } else {
    payload.priceType = SellableProductPriceType.InGameCurrency;
    payload.iosPrice.inGamePrice = price.ios;
    payload.androidPrice.inGamePrice = price.android;
    payload.iosPrice.IAPId = null;
    payload.androidPrice.IAPId = null;
    payload.iosPrice.rvQuantity = null;
    payload.androidPrice.rvQuantity = null;
  }

  await updateReward(offerSpin.id, payload);
};

const handleFreeRewardChange = async (offerSpin: OfferSpin, isPayLater?: boolean) => {
  const payload = mapOfferSpinInput(offerSpin);

  if (isPayLater && payload.reRollIosPrice && payload.reRollAndroidPrice) {
    payload.reRollPriceType = SellableProductPriceType.Free;
    payload.reRollIosPrice.inGamePrice = null;
    payload.reRollAndroidPrice.inGamePrice = null;
    payload.reRollIosPrice.IAPId = null;
    payload.reRollAndroidPrice.IAPId = null;
    payload.reRollIosPrice.rvQuantity = null;
    payload.reRollAndroidPrice.rvQuantity = null;
  } else {
    payload.priceType = SellableProductPriceType.Free;
    payload.iosPrice.IAPId = null;
    payload.androidPrice.IAPId = null;
    payload.iosPrice.rvQuantity = null;
    payload.androidPrice.rvQuantity = null;
    payload.iosPrice.inGamePrice = null;
    payload.androidPrice.inGamePrice = null;
  }

  await updateReward(offerSpin.id, payload);
};

const handlePriceTypeChange = async (value: SellableProductPriceType) => {
  selectedPriceType.value = value;
};
</script>

<style module lang="scss">
.theadStyle {
  background-color: var(--vd-color-text-light-9) !important;
  border: 2px solid var(--vd-color-text-light-9) !important;
}

.priceHeader {
  display: flex;
  align-items: center;
  width: 100%;
  span {
    width: 50%;
  }
}

.spinLimitForm {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinLimit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  min-width: 100px;
}
</style>
